import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  Avatar,
  Paper,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';
import {
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Lock as LockIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  MedicalServices as MedicalIcon,
  Assignment as AssignmentIcon,
  BugReport as BugReportIcon,
  Science as ScienceIcon,
  Cancel as CancelIcon,
  Note as NoteIcon
} from '@mui/icons-material';
import { Visit, VisitAddendum } from '@services/visitsService';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';

interface VisitTimelineProps {
  visit: Visit;
  addenda: VisitAddendum[];
  showFullContent?: boolean;
}

const VisitTimeline: React.FC<VisitTimelineProps> = ({
  visit,
  addenda,
  showFullContent = false
}) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft':
        return <EditIcon />;
      case 'signed':
        return <CheckCircleIcon />;
      case 'locked':
        return <LockIcon />;
      default:
        return <EditIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'default';
      case 'signed':
        return 'success';
      case 'locked':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft':
        return 'Draft';
      case 'signed':
        return 'Semnata';
      case 'locked':
        return 'Blocata';
      default:
        return status;
    }
  };

  const getAddendumIcon = (type: string) => {
    switch (type) {
      case 'correction':
        return <BugReportIcon />;
      case 'completion':
        return <AddIcon />;
      case 'laboratory_result':
        return <ScienceIcon />;
      case 'prescription_void':
        return <CancelIcon />;
      default:
        return <NoteIcon />;
    }
  };

  const getAddendumColor = (type: string) => {
    switch (type) {
      case 'correction':
        return 'error';
      case 'completion':
        return 'info';
      case 'laboratory_result':
        return 'success';
      case 'prescription_void':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getAddendumLabel = (type: string) => {
    switch (type) {
      case 'correction':
        return 'Corectie';
      case 'completion':
        return 'Completare';
      case 'laboratory_result':
        return 'Rezultat laborator';
      case 'prescription_void':
        return 'Anulare reteta';
      default:
        return 'Addendum';
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd MMM yyyy, HH:mm', { locale: ro });
    } catch {
      return dateString;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd MMM yyyy', { locale: ro });
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AssignmentIcon />
        Timeline Consultatie
      </Typography>

      <Timeline position="right">
        {/* Crearea consultatiei */}
        <TimelineItem>
          <TimelineOppositeContent
            sx={{ m: 'auto 0' }}
          >
            {formatDateTime(visit.created_at || visit.createdAt)}
          </TimelineOppositeContent>
          <TimelineSeparator>
            <TimelineConnector />
            <TimelineDot color="primary">
              <MedicalIcon />
            </TimelineDot>
            <TimelineConnector />
          </TimelineSeparator>
          <TimelineContent sx={{ py: '12px', px: 2 }}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6" component="span">
                    Consultatie creata
                  </Typography>
                  <Chip
                    icon={getStatusIcon(visit.status)}
                    label={getStatusLabel(visit.status)}
                    size="small"
                    color={getStatusColor(visit.status) as any}
                    variant="outlined"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {visit.visit_type} • {formatDate(visit.visit_date || visit.createdAt)}
                </Typography>
                {visit.chief_complaint && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Motiv:</strong> {visit.chief_complaint}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </TimelineContent>
        </TimelineItem>

        {/* Semnarea consultatiei */}
        {visit.signed_at && (
          <TimelineItem>
            <TimelineOppositeContent
              sx={{ m: 'auto 0' }}
            >
              {formatDateTime(visit.signed_at || '')}
            </TimelineOppositeContent>
            <TimelineSeparator>
              <TimelineConnector />
              <TimelineDot color="success">
                <CheckCircleIcon />
              </TimelineDot>
              <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent sx={{ py: '12px', px: 2 }}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h6" component="span">
                      Consultatie semnata
                    </Typography>
                    <Chip
                      icon={<ScheduleIcon />}
                      label="Countdown activ"
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Semnata de: {visit.signed_by ? 'Dr. Veterinar' : 'Sistem'}
                  </Typography>
                  {visit.signature_notes && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Note:</strong> {visit.signature_notes}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </TimelineContent>
          </TimelineItem>
        )}

        {/* Blocarea consultatiei */}
        {visit.locked_at && (
          <TimelineItem>
            <TimelineOppositeContent
              sx={{ m: 'auto 0' }}
            >
              {formatDateTime(visit.locked_at || '')}
            </TimelineOppositeContent>
            <TimelineSeparator>
              <TimelineConnector />
              <TimelineDot color="error">
                <LockIcon />
              </TimelineDot>
              <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent sx={{ py: '12px', px: 2 }}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h6" component="span">
                      Consultatie blocata
                    </Typography>
                    <Chip
                      icon={<LockIcon />}
                      label="Blocata permanent"
                      size="small"
                      color="error"
                      variant="filled"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Blocata automat dupa 12 ore de la semnare
                  </Typography>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <AlertTitle>Modificari limitate</AlertTitle>
                    Consultatia nu mai poate fi modificata. Foloseste addendum-uri pentru corectii.
                  </Alert>
                </CardContent>
              </Card>
            </TimelineContent>
          </TimelineItem>
        )}

        {/* Addendum-uri */}
        {addenda.map((addendum, index) => (
          <TimelineItem key={addendum.id}>
            <TimelineOppositeContent
              sx={{ m: 'auto 0' }}
            >
              {formatDateTime(addendum.created_at || addendum.createdAt)}
            </TimelineOppositeContent>
            <TimelineSeparator>
              <TimelineConnector />
              <TimelineDot color={getAddendumColor(addendum.type) as any}>
                {getAddendumIcon(addendum.type)}
              </TimelineDot>
              {index < addenda.length - 1 && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent sx={{ py: '12px', px: 2 }}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h6" component="span">
                      {getAddendumLabel(addendum.type)}
                    </Typography>
                    <Chip
                      icon={getAddendumIcon(addendum.type)}
                      label={getAddendumLabel(addendum.type)}
                      size="small"
                      color={getAddendumColor(addendum.type) as any}
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Adaugat de: Dr. {addendum.author_vet_id}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Motiv:</strong> {addendum.reason}
                  </Typography>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      backgroundColor: 'grey.50',
                      border: '1px solid',
                      borderColor: 'grey.200'
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {addendum.content}
                    </Typography>
                  </Paper>
                  {addendum.metadata && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Metadate: {JSON.stringify(addendum.metadata)}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>

      {addenda.length === 0 && visit.status === 'locked' && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <AlertTitle>Nicio modificare ulterioara</AlertTitle>
          Consultatia nu are addendum-uri. Toate modificarile au fost facute inainte de blocare.
        </Alert>
      )}
    </Box>
  );
};

export default VisitTimeline;
